import { enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { maskConfig } from './app/app.module';
import { environment } from './environments/environment';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginator } from './app/commun/CustomPaginator';
import { ImarcheToken } from './app/marche/services/imarche.token';
import { MarcheAdapterService } from './app/marche/services/marche-adapter.service';
import { provideNgxMask, NgxMaskDirective } from 'ngx-mask';
import { roleInterceptorProvider } from './app/HttpInterceptor/RoleInterceptorProvider';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { AppRoutingModule } from './app/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AuthModule } from './app/modules/auth/auth.module';
import { ApiModule } from 'openapi/build/api.module';
import { apiConfigFactory } from './app/services/config/api-configuration.service';
import { MaterialLayoutModule } from './app/modules/material-layout/material-layout.module';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app/app.component';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  includeBearerTokenInterceptor,
  IncludeBearerTokenCondition
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { fournirKeycloak } from './app/services/security/keycloak.providers';

import { StatutCommandeService } from './app/services/commande/statut-commande.service';
import { firstValueFrom } from 'rxjs';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule, AppRoutingModule, AuthModule, ApiModule.forRoot(apiConfigFactory), MaterialLayoutModule, ToastrModule.forRoot({ positionClass: 'toast-bottom-right' }), NgxMaskDirective),
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
        { provide: MatPaginatorIntl, useClass: CustomPaginator },
        { provide: ImarcheToken, useClass: MarcheAdapterService },
        provideNgxMask(maskConfig),
        roleInterceptorProvider,
        {
            provide: MAT_CHIPS_DEFAULT_OPTIONS,
            useValue: { separatorKeyCodes: [13, 188], selectable: true, removable: true }
        },
        // Migraiton keycloak-angular 19 à 20 : on remplace KeycloakAngularModule + InitializerKcFactory
        // par provideKeycloak qui est la nouvelle API standalone recommandée
        ...fournirKeycloak(),
        // On recrée le meme comportement qu'avant pour le refresh du token :
        // setInterval toutes les 14 min + refresh immediat quand le token expire
        provideAppInitializer(() => {
            const keycloak = inject(Keycloak);

            const updateToken = async (): Promise<void> => {
                try {
                    await keycloak.updateToken(3000);
                } catch (error) {
                    console.error('Failed to refresh token', error);
                    throw error;
                }
            };

            const vinghtHuitMinutes = 14 * 60 * 1000;
            setInterval(() => {
                console.log('trying to refresh token ' + new Date());
                updateToken()
                    .then(() => console.log('refreshed ' + new Date()))
                    .catch(() => console.error('Failed to refresh token ' + new Date()));
            }, vinghtHuitMinutes);

            keycloak.onTokenExpired = () => {
                console.log('Token expired ' + new Date());
                updateToken()
                    .then(() => console.log('refreshed ' + new Date()))
                    .catch(() => console.error('Failed to refresh token ' + new Date()));
            };
        }),
        {
            provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
            useValue: [
                createInterceptorCondition<IncludeBearerTokenCondition>({
                    urlPattern: /^(?!.*\/assets)(?!.*\/clients\/public).*$/
                })
            ]
        },
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
        provideAnimations(),
        provideZoneChangeDetection({ eventCoalescing: true }),
      // Intercepteur bearer token fonctionnel (nouvelle API v20)
      // remplace enableBearerInterceptor:true, on exclut /assets et /clients/public comme avant
      provideAppInitializer(async () => {
        const keycloak = inject(Keycloak);
        const statutCommandeService = inject(StatutCommandeService);

        // On attend que Keycloak soit initialisé.
        // provideKeycloak utilise un initialiseur qui appelle keycloak.init().
        // On peut s'assurer que l'instance est prête.
        try {
          // Si on est déjà authentifié ou si on attend la fin de l'init
          // provideKeycloak garantit que l'init est lancé.
          // On peut faire un petit wait si nécessaire ou simplement appeler getStatuts
          // car l'intercepteur includeBearerTokenInterceptor attendra le token.
          // Mais ici le problème est la 401, donc le token n'est pas prêt.

          // Dans keycloak-js, keycloak.authenticated est défini après init()
          // On attend que le token soit disponible
          let retry = 0;
          while (!keycloak.token && retry < 10) {
            await new Promise(resolve => setTimeout(resolve, 100));
            retry++;
          }

          await firstValueFrom(statutCommandeService.getStatuts());
        } catch (e) {
          console.error('Erreur lors du pré-chargement des statuts', e);
        }
      })
    ]
})
  .catch(err => console.error(err));
